import {
  AuthSessionResponseSchema,
  EntitlementsPayloadSchema,
  RegisterResponseSchema,
  WsTicketResponseSchema,
  type Entitlements,
  type Platform,
  type User,
} from "@legacy-manager/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { mensagemDoCodigo, t } from "../i18n";
import { ClienteDaApi, ErroDaApi, guardaDeNavegador, type GuardaDeSessao } from "./cliente";
import { esquecerLicenca, idDoAparelho } from "./licenca";
import { SocketDaLiga } from "./socket";
import { pedirTicketDaSteam } from "./steam";
import { PerfilSchema, type Perfil } from "./esquemas";

/**
 * A sessão do modo online, num contexto só.
 *
 * Quem segura a conexão é este arquivo, e só ele. As telas pedem `useSessao()`
 * e recebem o cliente da API já com token, o socket já conectado e quem está
 * logado. Nenhuma tela guarda token, nem monta cabeçalho, nem sabe renovar
 * nada.
 *
 * Três decisões moram aqui:
 *
 * O **token de acesso nunca é gravado em disco**. Ele vive na memória do
 * cliente da API. O refresh é responsabilidade do guarda: no navegador ele é um
 * cookie `httpOnly` que o JavaScript nem enxerga, e nos aplicativos nativos é o
 * chaveiro do sistema (docs/SECURITY.md 2).
 *
 * A **sessão é restaurada por renovação explícita**. Ao abrir o jogo não existe
 * token na memória, então o primeiro passo é pedir `POST /v1/auth/refresh`. No
 * navegador o cookie responde sozinho; no aplicativo nativo o guarda entrega o
 * refresh guardado. Se nada responder, a pessoa é visitante e pronto.
 *
 * O **socket acompanha a sessão**. Sobe quando ela existe, cai quando ela
 * acaba, e é o mesmo para o jogo inteiro: uma conexão por pessoa, não uma por
 * tela (docs/WEBSOCKET.md 1).
 */

/** Versão do cliente enviada em todo pedido, para o servidor recusar build velha. */
const VERSAO_DO_CLIENTE = import.meta.env.VITE_APP_VERSION ?? "0.1.0";

/**
 * Versão do motor enviada em todo pedido.
 *
 * Vem do ambiente e não de `import ENGINE_VERSION`: o motor só é carregado no
 * Web Worker do modo solo, e importá-lo aqui o arrastaria para o pacote inicial
 * de quem só quer entrar numa liga.
 */
const VERSAO_DO_MOTOR = import.meta.env.VITE_ENGINE_VERSION ?? "0.1.0";

/**
 * Versão dos termos que a tela de cadastro apresenta (docs/LEGAL.md 2).
 *
 * O servidor grava o que o cliente informar, sem conferir contra uma lista, por
 * isso o número precisa sair de um lugar só.
 */
export const VERSAO_DOS_TERMOS = "2026-09-21";

/** Endereço da API. Sem configuração, a mesma origem que serviu o jogo. */
function enderecoDaApi(): string {
  const configurado = import.meta.env.VITE_API_URL?.replace(/\/+$/, "");
  if (configurado) return configurado;
  return typeof window === "undefined" ? "http://localhost:3000" : window.location.origin;
}

/**
 * Plataforma deste aparelho.
 *
 * O servidor usa isso para decidir onde o refresh viaja: `web` recebe cookie,
 * o resto recebe o token no corpo (docs/SECURITY.md 2). Errar aqui deixaria o
 * aplicativo nativo sem nada para guardar.
 */
function plataforma(): Platform {
  if (typeof window === "undefined") return "web";
  if ("__TAURI_INTERNALS__" in window) return "desktop";
  const agente = navigator.userAgent ?? "";
  if (/Android/i.test(agente)) return "android";
  if (/iPhone|iPad|iPod/i.test(agente)) return "ios";
  return "web";
}

/**
 * Sinal do aparelho, para a detecção de contas múltiplas.
 *
 * Existe por um motivo concreto: no ranqueado, criar cinco contas e deixar
 * quatro perderem de propósito para a quinta destrói a competição inteira. E
 * numa liga de amigos, duas contas da mesma pessoa viram um clube com dois
 * elencos.
 *
 * **Só o que o navegador entrega sem pedir permissão nenhuma.** Nada de canvas,
 * nada de WebGL, nada de lista de fontes. Separariam um pouco melhor e virariam
 * rastreamento, que é exatamente o que `docs/LEGAL.md` 1.4 promete que o jogo
 * não faz. O ganho não paga a promessa quebrada.
 *
 * A resolução vai arredondada e a memória em gigabytes inteiros de propósito: o
 * objetivo é **agrupar aparelhos parecidos, não separar um do outro**. O
 * servidor guarda só um hash com sal do conjunto, nunca os valores.
 *
 * Qualquer coisa que falhe devolve `undefined` e o campo simplesmente não vai.
 * Omitir é aceito pelo servidor, e antifraude nunca pode impedir alguém de
 * entrar na própria conta.
 */
function sinalDoAparelho(): Record<string, unknown> | undefined {
  if (typeof window === "undefined" || typeof navigator === "undefined") return undefined;
  try {
    const fuso = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const idioma = navigator.language;
    const tela = window.screen ? `${window.screen.width}x${window.screen.height}` : null;
    if (!fuso || !idioma || !tela) return undefined;

    const sinal: Record<string, unknown> = { timezone: fuso, language: idioma, screen: tela };

    const nucleos = navigator.hardwareConcurrency;
    if (typeof nucleos === "number" && nucleos > 0) sinal["cores"] = nucleos;

    const memoria = (navigator as { deviceMemory?: number }).deviceMemory;
    if (typeof memoria === "number" && memoria > 0) sinal["memory"] = Math.round(memoria);

    const sistema = navigator.platform;
    if (sistema) sinal["platform"] = sistema;

    return sinal;
  } catch {
    return undefined;
  }
}

/**
 * Nome e plataforma do aparelho, como eles aparecem na lista de dispositivos.
 *
 * O aplicativo instalado manda também o id de instalação, o mesmo que a
 * licença registra: é o único sinal que quer dizer "mesmo aparelho" de
 * verdade para a detecção de contas múltiplas. O navegador não manda: lá o
 * id viveria no `localStorage`, e a política de privacidade promete que o
 * jogo não guarda identificador nenhum no navegador para isso.
 */
async function aparelho(): Promise<{
  name: string;
  platform: Platform;
  fingerprint?: Record<string, unknown>;
  installId?: string;
}> {
  const qual = plataforma();
  const sinal = sinalDoAparelho();
  const instalacao = qual === "web" ? null : await idDoAparelho().catch(() => null);
  return {
    name: t(`conta.aparelhos.${qual}`),
    platform: qual,
    ...(sinal ? { fingerprint: sinal } : {}),
    ...(instalacao ? { installId: instalacao } : {}),
  };
}

// ---------------------------------------------------------------------------
// O contexto
// ---------------------------------------------------------------------------

export type EstadoDaSessao = "carregando" | "visitante" | "conectada";

export interface DadosDaConta {
  usuario: User;
  direitos: Entitlements;
  notificacoes: Record<string, boolean>;
}

/** Pedido do socket para recarregar um canal pela API (docs/WEBSOCKET.md 3). */
export interface PedidoDeRecarga {
  canal: string;
  em: number;
}

export interface Sessao {
  estado: EstadoDaSessao;
  conta: DadosDaConta | null;
  cliente: ClienteDaApi;
  socket: SocketDaLiga;
  /** Verdadeiro enquanto o socket está de pé. */
  aoVivo: boolean;
  /** O protocolo do servidor não bate com o desta build. */
  precisaAtualizar: boolean;
  /** Último canal que perdeu eventos e precisa ser relido pela API. */
  recarga: PedidoDeRecarga | null;
  entrar: (email: string, senha: string) => Promise<void>;
  /**
   * Entra pela Steam, sem ninguém digitar nada.
   *
   * Devolve `false` quando não há Steam por perto, que é o caso comum: no
   * navegador, no celular e numa build sem o SDK. Quem chama cai no caminho
   * normal de entrar na conta.
   */
  entrarPelaSteam: () => Promise<boolean>;
  criarConta: (dados: {
    email: string;
    senha: string;
    nome: string;
    chaveDoAlfa?: string;
  }) => Promise<User>;
  sair: () => Promise<void>;
  recarregarConta: () => Promise<void>;
  /** Guarda o perfil que uma tela acabou de alterar, sem ida extra à rede. */
  aplicarPerfil: (perfil: Perfil) => void;
}

const Contexto = createContext<Sessao | null>(null);

/**
 * Relógio que anda sozinho, para as contagens regressivas de prazo.
 *
 * Mora aqui porque é o mesmo relógio da lista de ligas, do saguão e do hub:
 * três telas contando o mesmo prazo não podem discordar em um segundo. Parado,
 * não agenda nada, então uma tela de fundo não gasta bateria à toa.
 */
export function useRelogio(ativo = true, intervaloMs = 1000): number {
  const [agora, definirAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return;
    const id = window.setInterval(() => definirAgora(Date.now()), intervaloMs);
    return () => window.clearInterval(id);
  }, [ativo, intervaloMs]);
  return agora;
}

/**
 * Texto de erro para mostrar ao jogador.
 *
 * Quem escolhe a redação é o **código** do envelope (docs/API.md 1), não a
 * mensagem. A mensagem do servidor é sempre em português, e uma tela em inglês
 * com um aviso em português é pior do que não ter tradução: parece defeito.
 *
 * Código que o dicionário não conhece cai de volta na mensagem do servidor.
 * Isso é caso real, e não defensivo à toa: servidor com código novo e cliente
 * ainda não atualizado acontece em todo lançamento, e nessa hora a frase do
 * servidor é a única coisa que explica o que houve. O texto genérico fica para
 * o que nem chegou a virar resposta, como uma falha dentro do navegador.
 */
export function mensagemDoErro(causa: unknown): string {
  if (!(causa instanceof ErroDaApi)) return t("online.erroInesperado");
  return mensagemDoCodigo(causa.code) ?? causa.message;
}

/** A sessão do modo online. Fora do provedor, é erro de montagem da árvore. */
export function useSessao(): Sessao {
  const sessao = useContext(Contexto);
  if (!sessao) throw new Error("useSessao precisa do ProvedorDeSessao acima na árvore.");
  return sessao;
}

// ---------------------------------------------------------------------------
// Conversas com a rede
// ---------------------------------------------------------------------------

function contaDoPerfil(perfil: Perfil): DadosDaConta {
  return {
    usuario: perfil.user,
    direitos: perfil.entitlements,
    notificacoes: perfil.notificationPrefs,
  };
}

function lerPerfil(cliente: ClienteDaApi): Promise<Perfil> {
  return cliente.pedir({ metodo: "GET", caminho: "/v1/me" }, PerfilSchema);
}

/**
 * Renova o acesso e devolve quem está logado.
 *
 * Rota pública de propósito: não há token para mandar, e uma falha aqui é
 * resposta legítima, não sessão perdida.
 */
async function renovarAcesso(cliente: ClienteDaApi, guarda: GuardaDeSessao): Promise<User | null> {
  const refresh = await guarda.lerRefresh().catch(() => null);
  try {
    const sessao = await cliente.pedir(
      {
        metodo: "POST",
        caminho: "/v1/auth/refresh",
        corpo: refresh ? { refreshToken: refresh } : {},
        publica: true,
      },
      AuthSessionResponseSchema,
    );
    cliente.definirAcesso(sessao.accessToken);
    if (sessao.refreshToken) await guarda.gravarRefresh(sessao.refreshToken);
    return sessao.user;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// O provedor
// ---------------------------------------------------------------------------

export function ProvedorDeSessao({
  children,
  guarda = guardaDeNavegador,
}: {
  children: ReactNode;
  /** Trocado pelo armazenamento seguro do sistema nos aplicativos nativos. */
  guarda?: GuardaDeSessao;
}): ReactNode {
  const [estado, definirEstado] = useState<EstadoDaSessao>("carregando");
  const [conta, definirConta] = useState<DadosDaConta | null>(null);
  const [aoVivo, definirAoVivo] = useState(false);
  const [precisaAtualizar, definirPrecisaAtualizar] = useState(false);
  const [recarga, definirRecarga] = useState<PedidoDeRecarga | null>(null);

  /*
   * O cliente e o socket nascem uma vez e vivem enquanto o jogo estiver aberto.
   * Recriá-los a cada desenho jogaria fora o token em memória e derrubaria a
   * conexão a cada mudança de estado, que é o oposto do que eles existem para
   * fazer.
   */
  const [rede] = useState(() => {
    const base = enderecoDaApi();
    // Caixa porque o cliente precisa derrubar o socket e o socket precisa do
    // cliente para pedir ticket. Um dos dois tem que ser preenchido depois.
    const caixa: { socket: SocketDaLiga | null } = { socket: null };

    const cliente = new ClienteDaApi({
      baseUrl: base,
      guarda,
      clientVersion: VERSAO_DO_CLIENTE,
      engineVersion: VERSAO_DO_MOTOR,
      aoPerderSessao: () => {
        caixa.socket?.desconectar();
        definirConta(null);
        definirEstado("visitante");
      },
    });

    const socket = new SocketDaLiga({
      baseUrl: base,
      pedirTicket: async () => {
        const resposta = await cliente.pedir(
          { metodo: "POST", caminho: "/v1/ws/ticket" },
          WsTicketResponseSchema,
        );
        return resposta.ticket;
      },
      aoConectar: () => definirAoVivo(true),
      aoCair: () => definirAoVivo(false),
      aoPrecisarRecarregar: (canal) => definirRecarga({ canal, em: Date.now() }),
      aoExigirAtualizacao: () => definirPrecisaAtualizar(true),
    });

    caixa.socket = socket;
    return { cliente, socket };
  });

  const recarregarConta = useCallback(async () => {
    const perfil = await lerPerfil(rede.cliente);
    definirConta(contaDoPerfil(perfil));
  }, [rede]);

  const sair = useCallback(async () => {
    // A saída local acontece mesmo se o servidor não responder: uma pessoa que
    // clicou em sair não pode continuar vendo a própria liga por falha de rede.
    try {
      await rede.cliente.pedirVazio({ metodo: "POST", caminho: "/v1/auth/logout" });
    } catch {
      // Sessão já morta do outro lado. Nada a fazer aqui.
    }
    await guarda.gravarRefresh(null).catch(() => undefined);
    // A licença deste aparelho sai junto com a conta: sem isso, quem saiu
    // continuaria jogando com o bilhete de quem entrou antes.
    esquecerLicenca();
    rede.socket.desconectar();
    rede.cliente.definirAcesso(null);
    definirConta(null);
    definirEstado("visitante");
  }, [rede, guarda]);

  const entrar = useCallback(
    async (email: string, senha: string) => {
      const sessao = await rede.cliente.pedir(
        {
          metodo: "POST",
          caminho: "/v1/auth/login",
          corpo: { email, password: senha, device: await aparelho() },
          publica: true,
        },
        AuthSessionResponseSchema,
      );
      rede.cliente.definirAcesso(sessao.accessToken);
      if (sessao.refreshToken) await guarda.gravarRefresh(sessao.refreshToken);

      const perfil = await lerPerfil(rede.cliente);
      definirConta(contaDoPerfil(perfil));
      definirEstado("conectada");
    },
    [rede, guarda],
  );

  /**
   * A entrada da Steam.
   *
   * O ticket vem da casca do Tauri e vale uma vez: se o servidor recusar,
   * insistir com o mesmo ticket não adianta, então a falha é silenciosa e a
   * tela normal aparece. Quem chegou aqui sem Steam nem paga a chamada.
   */
  const entrarPelaSteam = useCallback(async () => {
    const daSteam = await pedirTicketDaSteam();
    if (!daSteam) return false;

    try {
      const sessao = await rede.cliente.pedir(
        {
          metodo: "POST",
          caminho: "/v1/auth/steam",
          corpo: { ticket: daSteam.ticket, device: await aparelho() },
          publica: true,
        },
        AuthSessionResponseSchema,
      );
      rede.cliente.definirAcesso(sessao.accessToken);
      if (sessao.refreshToken) await guarda.gravarRefresh(sessao.refreshToken);

      const perfil = await lerPerfil(rede.cliente);
      definirConta(contaDoPerfil(perfil));
      definirEstado("conectada");
      return true;
    } catch {
      return false;
    }
  }, [rede, guarda]);

  const criarConta = useCallback(
    async (dados: { email: string; senha: string; nome: string; chaveDoAlfa?: string }) => {
      const criada = await rede.cliente.pedir(
        {
          metodo: "POST",
          caminho: "/v1/auth/register",
          corpo: {
            email: dados.email,
            password: dados.senha,
            displayName: dados.nome,
            acceptedTermsVersion: VERSAO_DOS_TERMOS,
            /*
             * Só vai quando existe.
             *
             * Enquanto o alfa estiver fechado o servidor cobra a chave, e
             * quando abrir ele para de cobrar sozinho, pela bandeira. Mandar
             * `undefined` num cadastro sem chave é o que permite as duas
             * coisas sem o cliente precisar saber em que fase o jogo está.
             */
            ...(dados.chaveDoAlfa ? { alphaKey: dados.chaveDoAlfa } : {}),
          },
          publica: true,
        },
        RegisterResponseSchema,
      );
      return criada.user;
    },
    [rede],
  );

  const aplicarPerfil = useCallback((perfil: Perfil) => {
    definirConta(contaDoPerfil(perfil));
  }, []);

  /*
   * Restauração da sessão, uma vez por abertura do jogo.
   *
   * Sem isto, fechar a aba e voltar exigiria digitar a senha de novo, mesmo com
   * o refresh válido guardado.
   *
   * **Uma vez quer dizer uma chamada, e não uma escrita de estado.** O refresh
   * gira: a resposta traz um token novo e invalida o que foi usado. Um segundo
   * pedido com o token velho não é um pedido repetido inofensivo, é exatamente
   * o que um token roubado pareceria, e o servidor faz o certo ao derrubar a
   * sessão inteira. Recarregar a página deslogava por isso.
   *
   * Quem impede a segunda chamada é a marca numa `ref`, que sobrevive ao
   * monta-desmonta-monta do `StrictMode` e protege também o dia em que uma das
   * dependências trocar de identidade em produção.
   *
   * **Não há bandeira de cancelamento aqui, e é de propósito.** A que existia
   * antes só descartava a resposta, depois de a chamada já ter saído, e com a
   * marca no lugar ela passou a fazer estrago: o desmonte do `StrictMode`
   * cancelava a única execução que ia acontecer, e o jogo ficava carregando
   * para sempre. Quem monta este provedor é a raiz do aplicativo, então não
   * existe desmonte que valha cancelar: ou o jogo está aberto, ou não há mais
   * tela para atualizar.
   */
  const jaRestaurou = useRef(false);

  useEffect(() => {
    if (jaRestaurou.current) return;
    jaRestaurou.current = true;

    void (async () => {
      const usuario = await renovarAcesso(rede.cliente, guarda);
      if (!usuario) {
        // Sem sessão guardada, a Steam é a próxima tentativa, e não a tela de
        // entrar: quem abriu o jogo por lá já provou quem é e que comprou.
        const pelaSteam = await entrarPelaSteam();
        if (!pelaSteam) definirEstado("visitante");
        return;
      }
      const perfil = await lerPerfil(rede.cliente).catch(() => null);
      if (!perfil) {
        definirEstado("visitante");
        return;
      }
      definirConta(contaDoPerfil(perfil));
      definirEstado("conectada");
    })();
  }, [rede, guarda, entrarPelaSteam]);

  // O socket existe enquanto a sessão existir, e some junto com ela.
  useEffect(() => {
    if (estado !== "conectada") return;
    void rede.socket.conectar();
    return () => {
      rede.socket.desconectar();
      definirAoVivo(false);
    };
  }, [estado, rede]);

  /*
   * O canal `user:{id}` é assinado sozinho no `hello`, então basta ouvir. São
   * os dois avisos que mexem na sessão inteira: compra confirmada e sessão
   * revogada de outro aparelho.
   */
  useEffect(() => {
    return rede.socket.ouvir((tipo, carga) => {
      if (tipo === "user.session.revoked") {
        void sair();
        return;
      }
      if (tipo === "user.entitlements") {
        const lido = EntitlementsPayloadSchema.safeParse(carga);
        if (!lido.success) return;
        definirConta((atual) => (atual ? { ...atual, direitos: lido.data.entitlements } : atual));
      }
    });
  }, [rede, sair]);

  const valor = useMemo<Sessao>(
    () => ({
      estado,
      conta,
      cliente: rede.cliente,
      socket: rede.socket,
      aoVivo,
      precisaAtualizar,
      recarga,
      entrar,
      entrarPelaSteam,
      criarConta,
      sair,
      recarregarConta,
      aplicarPerfil,
    }),
    [
      estado,
      conta,
      rede,
      aoVivo,
      precisaAtualizar,
      recarga,
      entrar,
      entrarPelaSteam,
      criarConta,
      sair,
      recarregarConta,
      aplicarPerfil,
    ],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}
